import {
  urlForWelfareInstances,
  urlForWelfareDefinitions,
  urlForWelfareOccurrencePolicies,
  urlForWelfareOccurrencePolicy,
  urlForGenerateWelfareInstances,
  urlForCancelWelfareInstance,
  urlForWelfareDefinitionDelete,
} from "@src/features/welfare/welfare-api-urls";
import {
  type GetAllWelfareInstancesArgs,
  type GetAllWelfareDefinitionsArgs,
  type GetWelfareOccurrencePoliciesArgs,
  type CreateWelfareOccurrencePolicyArgs,
  type DeleteWelfareOccurrencePolicyArgs,
  type GenerateInstancesArgs,
  type GenerateInstancesResponse,
  type CancelWelfareInstanceArgs,
  type CreateWelfareDefinitionArgs,
  type DeleteWelfareDefinitionArgs,
} from "@src/features/welfare/welfare-dtos";
import {
  type AllWelfareInstancesResponse,
  type AllWelfareDefinitionsResponse,
  type WelfareOccurrencePoliciesResponse,
  type WelfareOccurrencePolicy,
  type WelfareDefinition,
} from "@src/features/welfare/welfare-models";
import { appApi } from "@src/integrations/redux/app-api";
import { HTTP_METHODS } from "@src/utils/network-constants";

export const welfareApi = appApi.injectEndpoints({
  tagTypes: ["WelfareOccPolicies", "WelfareInstances", "WelfareDefinitions"],
  endpoints: (builder) => ({
    getAllWelfareInstances: builder.query<
      AllWelfareInstancesResponse,
      GetAllWelfareInstancesArgs & { instanceType: string }
    >({
      query: ({ spaceId, instanceType, startDate, endDate }) => {
        const params = new URLSearchParams();
        params.set("instanceType", instanceType);
        if (startDate) params.set("startDate", startDate);
        if (endDate)   params.set("endDate", endDate);
        return {
          url: `${urlForWelfareInstances(spaceId)}?${params.toString()}`,
          method: HTTP_METHODS.GET,
        };
      },
      providesTags: (_result, _error, { spaceId }) => [
        { type: "WelfareInstances" as const, id: spaceId },
      ],
    }),

    getAllWelfareDefinitions: builder.query<
      AllWelfareDefinitionsResponse,
      GetAllWelfareDefinitionsArgs & { instanceType: string }
    >({
      query: ({ spaceId, instanceType }) => ({
        url: `${urlForWelfareDefinitions(spaceId)}?instanceType=${instanceType}`,
        method: HTTP_METHODS.GET,
      }),
      providesTags: (_result, _error, { spaceId }) => [
        { type: "WelfareDefinitions" as const, id: spaceId },
      ],
    }),

    getWelfareOccurrencePolicies: builder.query<
      WelfareOccurrencePoliciesResponse,
      GetWelfareOccurrencePoliciesArgs
    >({
      query: ({ spaceId, definitionId }) => ({
        url: urlForWelfareOccurrencePolicies(spaceId, definitionId),
        method: HTTP_METHODS.GET,
      }),
      providesTags: (result, _error, { definitionId }) =>
        result
          ? [
              { type: "WelfareOccPolicies" as const, id: definitionId },
              ...result.data.map(() => ({
                type: "WelfareOccPolicies" as const,
                id: definitionId,
              })),
            ]
          : [{ type: "WelfareOccPolicies" as const, id: definitionId }],
    }),

    deleteWelfareOccurrencePolicy: builder.mutation<
      void,
      DeleteWelfareOccurrencePolicyArgs
    >({
      query: ({ spaceId, definitionId, policyId }) => ({
        url: urlForWelfareOccurrencePolicy(spaceId, definitionId, policyId),
        method: HTTP_METHODS.DELETE,
      }),
      invalidatesTags: (_res, _err, { definitionId }) => [
        { type: "WelfareOccPolicies" as const, id: definitionId },
      ],
    }),

    createWelfareOccurrencePolicy: builder.mutation<
      { data: WelfareOccurrencePolicy },
      CreateWelfareOccurrencePolicyArgs
    >({
      query: (args) => ({
        url: urlForWelfareOccurrencePolicies(args.spaceId, args.definitionId),
        method: HTTP_METHODS.POST,
        body: {
          dayOfWeek: args.dayOfWeek,
          startHour: args.startHour,
          startMinute: args.startMinute,
          durationHours: args.durationHours,
          durationMinutes: args.durationMinutes,
          locationName: args.locationName,
          locationLat: args.locationLat,
          locationLng: args.locationLng,
        },
      }),
      invalidatesTags: (_res, _err, { definitionId }) => [
        { type: "WelfareOccPolicies" as const, id: definitionId },
      ],
    }),

    generateWelfareInstances: builder.mutation<
      GenerateInstancesResponse,
      GenerateInstancesArgs
    >({
      query: ({ spaceId, weeksAhead, instanceType }) => ({
        url: `${urlForGenerateWelfareInstances(spaceId)}?weeksAhead=${weeksAhead}&instanceType=${encodeURIComponent(instanceType)}`,
        method: HTTP_METHODS.POST,
      }),
      invalidatesTags: (_res, _err, { spaceId }) => [
        { type: "WelfareInstances" as const, id: spaceId },
      ],
    }),

    cancelWelfareInstance: builder.mutation<
      { cancelledId: string },
      CancelWelfareInstanceArgs
    >({
      query: ({ spaceId, instanceId }) => ({
        url: urlForCancelWelfareInstance(spaceId, instanceId),
        method: HTTP_METHODS.PATCH,
      }),
      invalidatesTags: (_res, _err, { spaceId }) => [
        { type: "WelfareInstances" as const, id: spaceId },
      ],
    }),

    createWelfareDefinition: builder.mutation<
      { data: WelfareDefinition },
      CreateWelfareDefinitionArgs
    >({
      query: ({ spaceId, ...body }) => ({
        url: urlForWelfareDefinitions(spaceId),
        method: HTTP_METHODS.POST,
        body,
      }),
      invalidatesTags: (_res, _err, { spaceId }) => [
        { type: "WelfareDefinitions" as const, id: spaceId },
      ],
    }),

    deleteWelfareDefinition: builder.mutation<
      void,
      DeleteWelfareDefinitionArgs
    >({
      query: ({ spaceId, definitionId }) => ({
        url: urlForWelfareDefinitionDelete(spaceId, definitionId),
        method: HTTP_METHODS.DELETE,
      }),
      invalidatesTags: (_res, _err, { spaceId }) => [
        { type: "WelfareDefinitions" as const, id: spaceId },
      ],
    }),
  }),
  overrideExisting: "throw",
});

export const {
  useGetAllWelfareInstancesQuery,
  useGetAllWelfareDefinitionsQuery,
  useGetWelfareOccurrencePoliciesQuery,
  useDeleteWelfareOccurrencePolicyMutation,
  useCreateWelfareOccurrencePolicyMutation,
  useGenerateWelfareInstancesMutation,
  useCancelWelfareInstanceMutation,
  useCreateWelfareDefinitionMutation,
  useDeleteWelfareDefinitionMutation,
} = welfareApi;
